import { type ReactNode } from "react";
import { Image, Linking, Pressable, Text, View } from "react-native";
import {
  ChevronRight,
  ExternalLink,
  Phone,
  Plus,
  UserRound,
} from "lucide-react-native";
import {
  CardSubtitleText,
  CardSurface,
  CardTitleText,
  IconButton,
  KebabMenu,
  MetaText,
  SectionTitle,
} from "@louise/moon-ui";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import type { CareTeamMember as ApiCareTeamMember } from "@src/packages/care/hooks/useCareTeam";
import type { CareConnection } from "@src/packages/patients/hooks/useCareConnections";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type CycleTeamMember = {
  id: string;
  kind: "person" | "organization";
  label: string;
  name: string;
  detail: string;
  organicIcon: OrganicMiniIconKind;
};

type ClinicContact = {
  phone?: string;
  portalUrl?: string;
};

type Props = {
  members: CycleTeamMember[];
  clinicName?: string | null;
  clinicContact?: ClinicContact;
  onEditTeam?: () => void;
  onAddProviderOrClinic?: () => void;
  onAddProvider?: () => void;
};

const avatarPlaceholder =
  require("@src/apps/moon/assets/images/avatar-placeholder.png") as number;

const TEXT = EMBER.colors.text;
const TEXT_LIGHT = EMBER.colors.textLight;

function getKnownCycleValue(value: string | null | undefined) {
  if (!value || value === "—") return null;
  return value;
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
}

export function buildCycleTeamMembers({
  members,
  clinicName,
  doctorName,
  recordedConnections = [],
}: {
  members: ApiCareTeamMember[] | undefined;
  clinicName?: string | null;
  doctorName?: string | null;
  recordedConnections?: CareConnection[];
}): CycleTeamMember[] {
  const knownClinicName = getKnownCycleValue(clinicName);
  const people: CycleTeamMember[] = (members ?? []).map((member) => ({
    id: member.id,
    kind: "person",
    label: member.role || "Provider",
    name: member.name,
    detail: [member.role, member.email, member.phoneNumber, knownClinicName]
      .filter(Boolean)
      .join(" · "),
    organicIcon: "concierge",
  }));

  const recordedMembers: CycleTeamMember[] = recordedConnections
    .filter((connection) => connection.kind === "provider")
    .map((connection) => ({
      id: `recorded-${connection.id}`,
      kind: "person",
      label: connection.role || "Provider",
      name: connection.name,
      detail: [
        connection.role,
        connection.specialty,
        connection.practiceName,
        connection.contactPhone,
        connection.contactEmail,
      ]
        .filter(Boolean)
        .join(" · "),
      organicIcon: "concierge",
    }));

  const knownDoctorName = getKnownCycleValue(doctorName);
  const hasDoctorFromCareTeam = knownDoctorName
    ? [...people, ...recordedMembers].some(
        (member) =>
          member.name.trim().toLowerCase() ===
          knownDoctorName.trim().toLowerCase(),
      )
    : false;

  if (knownDoctorName && !hasDoctorFromCareTeam) {
    people.unshift({
      id: `doctor-${slugify(knownDoctorName)}`,
      kind: "person",
      label: "REI",
      name: knownDoctorName,
      detail: knownClinicName
        ? `Reproductive endocrinologist · ${knownClinicName}`
        : "Reproductive endocrinologist",
      organicIcon: "concierge",
    });
  }

  const allPeople = [...people, ...recordedMembers];

  if (!knownClinicName) return allPeople;

  return [
    ...allPeople,
    {
      id: `clinic-${slugify(knownClinicName)}`,
      kind: "organization",
      label: "Clinic",
      name: knownClinicName,
      detail:
        allPeople.length > 0
          ? "Clinic connected to this cycle."
          : "Add providers, portal details, and cycle instructions.",
      organicIcon: "clinics",
    },
  ];
}

function translateCycleTeamMember(
  member: CycleTeamMember,
  t: TranslationFunction,
): CycleTeamMember {
  if (member.label === "REI") {
    const trailingClinic = member.detail.includes("·")
      ? ` · ${member.detail.split("·").slice(1).join("·").trim()}`
      : "";
    return {
      ...member,
      label: t("care.cycle_clinic_team.rei"),
      detail: `${t("care.cycle_clinic_team.reproductive_endocrinologist")}${trailingClinic}`,
    };
  }
  if (member.label === "Clinic") {
    const isClinicConnected =
      member.detail === "Clinic connected to this cycle.";
    const isAddProviders =
      member.detail ===
      "Add providers, portal details, and cycle instructions.";
    return {
      ...member,
      label: t("care.cycle_clinic_team.clinic"),
      detail: isClinicConnected
        ? t("care.cycle_clinic_team.clinic_connected")
        : isAddProviders
          ? t("care.cycle_clinic_team.add_providers_portal")
          : member.detail,
    };
  }
  return member;
}

function CycleTeamPersonCard({ member }: { member: CycleTeamMember }) {
  const t = useTranslations();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t("care.cycle_clinic_team.open_member", {
        params: { name: member.name },
      })}
      style={({ pressed }) => ({
        flexGrow: 1,
        flexBasis: "47%",
        minWidth: 128,
        paddingVertical: 6,
        alignItems: "center",
        gap: 8,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View
        style={{
          width: 48,
          height: 48,
          borderRadius: 24,
          borderWidth: 1,
          borderColor: semanticColors.border.glass,
          overflow: "hidden",
          backgroundColor: "rgba(255,255,255,0.26)",
        }}
      >
        <Image
          source={avatarPlaceholder}
          resizeMode="cover"
          style={{ width: 48, height: 48 }}
        />
      </View>
      <View style={{ alignItems: "center", minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 13,
            lineHeight: 16,
            color: TEXT,
            fontWeight: "900",
            textAlign: "center",
          }}
        >
          {member.name}
        </Text>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 10,
            lineHeight: 12,
            color: TEXT_LIGHT,
            fontWeight: "900",
            textTransform: "uppercase",
            letterSpacing: 0.25,
            marginTop: 2,
            textAlign: "center",
          }}
        >
          {member.label}
        </Text>
      </View>
    </Pressable>
  );
}

function CycleTeamOrganizationRow({ member }: { member: CycleTeamMember }) {
  const t = useTranslations();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t("care.cycle_clinic_team.open_member", {
        params: { name: member.name },
      })}
      style={({ pressed }) => ({
        paddingVertical: 6,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <OrganicMiniIcon kind={member.organicIcon} size={34} />
      <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <SectionTitle level="subsection">{member.label}</SectionTitle>
        <View>
          <CardTitleText numberOfLines={1}>{member.name}</CardTitleText>
          <MetaText numberOfLines={2}>{member.detail}</MetaText>
        </View>
      </View>
      <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.8} />
    </Pressable>
  );
}

function CycleTeamMenuButton({
  onEditTeam,
  onAddProviderOrClinic,
}: {
  onEditTeam: () => void;
  onAddProviderOrClinic: () => void;
}) {
  const t = useTranslations();

  return (
    <View style={{ flexShrink: 0 }}>
      <KebabMenu
        items={[
          {
            id: "edit_team",
            label: t("care.cycle_clinic_team.menu_edit_team"),
            icon: UserRound,
            onPress: onEditTeam,
            action: "cycle_team_menu_item",
          },
          {
            id: "add_provider",
            label: t("care.cycle_clinic_team.menu_add_provider"),
            icon: Plus,
            onPress: onAddProviderOrClinic,
            action: "cycle_team_menu_item",
          },
        ]}
        accessibilityLabel={t("care.cycle_clinic_team.open_actions")}
        triggerAction="care.cycle_clinic_team.open_actions"
        buttonColor={TEXT_LIGHT}
        triggerIconSize={19}
      />
    </View>
  );
}

function TeamSetupAction({
  icon,
  title,
  subtitle,
  onPress,
  comingSoon,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  onPress?: () => void;
  comingSoon?: boolean;
}) {
  const capturePress = usePressCapture("TeamSetupAction");
  return (
    <Pressable
      onPress={
        comingSoon || !onPress
          ? undefined
          : () => {
              // `title` is translated copy supplied by the caller.
              capturePress(pressLabel(title), "team_setup_action");
              onPress();
            }
      }
      accessibilityRole="button"
      accessibilityLabel={title}
      style={({ pressed }) => ({
        paddingVertical: 6,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        opacity: comingSoon ? 0.5 : pressed ? 0.76 : 1,
      })}
    >
      {icon}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 16,
            color: TEXT,
            fontWeight: "900",
          }}
        >
          {title}
        </Text>
        <CardSubtitleText>{subtitle}</CardSubtitleText>
      </View>
      <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.8} />
    </Pressable>
  );
}

export default function CycleClinicTeamCard({
  members,
  clinicName,
  clinicContact = {},
  onEditTeam,
  onAddProviderOrClinic,
  onAddProvider,
}: Props) {
  const t = useTranslations();
  const translatedMembers = members.map((member) =>
    translateCycleTeamMember(member, t),
  );
  const people = translatedMembers.filter((member) => member.kind === "person");
  const organizations = translatedMembers.filter(
    (member) => member.kind === "organization",
  );
  // The header already names the cycle's clinic, so the member list only
  // shows organizations other than that clinic.
  const clinicKey = clinicName?.trim().toLowerCase();
  const otherOrganizations = organizations.filter(
    (member) => member.name.trim().toLowerCase() !== clinicKey,
  );
  const hasTeamDetails =
    people.length > 0 || organizations.length > 0 || Boolean(clinicName);
  const displayClinicName =
    clinicName ?? t("care.cycle_clinic_team.connect_care_team_fallback");
  const showActions = Boolean(onEditTeam && onAddProviderOrClinic);

  return (
    <CardSurface style={{ padding: 14, gap: 12 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 }}>
        <OrganicMiniIcon kind="clinics" size={42} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              {hasTeamDetails ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 10,
                    lineHeight: 12,
                    color: TEXT_LIGHT,
                    fontWeight: "900",
                    textTransform: "uppercase",
                    letterSpacing: 0.25,
                  }}
                >
                  {t("care.cycle_clinic_team.clinic")}
                </Text>
              ) : null}
              <CardTitleText numberOfLines={1} style={{ marginTop: 2 }}>
                {displayClinicName}
              </CardTitleText>
            </View>
            {hasTeamDetails ? (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 4,
                  marginTop: -2,
                }}
              >
                {clinicContact.phone ? (
                  <IconButton
                    accessibilityLabel={t("care.cycle_clinic_team.call_clinic")}
                    action="care.cycle_clinic_team.call_clinic"
                    onPress={() => {
                      void Linking.openURL(`tel:${clinicContact.phone}`);
                    }}
                    hitSlop={8}
                    size="md"
                    surface="bare"
                    pressedOpacity={0.62}
                  >
                    <Phone size={17} color={TEXT_LIGHT} strokeWidth={1.9} />
                  </IconButton>
                ) : null}
                {clinicContact.portalUrl ? (
                  <IconButton
                    accessibilityLabel={t("care.cycle_clinic_team.open_portal")}
                    action="care.cycle_clinic_team.open_portal"
                    onPress={() => {
                      if (clinicContact.portalUrl) {
                        void Linking.openURL(clinicContact.portalUrl);
                      }
                    }}
                    hitSlop={8}
                    size="md"
                    surface="bare"
                    pressedOpacity={0.62}
                  >
                    <ExternalLink
                      size={16}
                      color={TEXT_LIGHT}
                      strokeWidth={1.9}
                    />
                  </IconButton>
                ) : null}
                {showActions && onEditTeam && onAddProviderOrClinic ? (
                  <CycleTeamMenuButton
                    onEditTeam={onEditTeam}
                    onAddProviderOrClinic={onAddProviderOrClinic}
                  />
                ) : null}
              </View>
            ) : null}
          </View>
          <CardSubtitleText>
            {hasTeamDetails
              ? t("care.cycle_clinic_team.team_subtitle_present")
              : t("care.cycle_clinic_team.team_subtitle_empty")}
          </CardSubtitleText>
        </View>
      </View>

      <View style={{ gap: 8 }}>
        {hasTeamDetails ? (
          <>
            {people.length > 0 ? (
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {people.map((member) => (
                  <CycleTeamPersonCard key={member.id} member={member} />
                ))}
              </View>
            ) : (
              <TeamSetupAction
                icon={<OrganicMiniIcon kind="concierge" size={30} />}
                title={t("care.cycle_clinic_team.add_physicians_title")}
                subtitle={t("care.cycle_clinic_team.add_physicians_subtitle")}
                onPress={onAddProvider ?? onAddProviderOrClinic}
                comingSoon={!onAddProvider && !onAddProviderOrClinic}
              />
            )}
            {otherOrganizations.map((member) => (
              <CycleTeamOrganizationRow key={member.id} member={member} />
            ))}
          </>
        ) : (
          <>
            <TeamSetupAction
              icon={<OrganicMiniIcon kind="clinics" size={30} />}
              title={t("care.cycle_clinic_team.connect_clinic_title")}
              subtitle={t("care.cycle_clinic_team.connect_clinic_subtitle")}
              onPress={onAddProviderOrClinic}
              comingSoon={!onAddProviderOrClinic}
            />
            <TeamSetupAction
              icon={<OrganicMiniIcon kind="concierge" size={30} />}
              title={t("care.cycle_clinic_team.add_physicians_title")}
              subtitle={t("care.cycle_clinic_team.add_physicians_subtitle")}
              onPress={onAddProvider ?? onAddProviderOrClinic}
              comingSoon={!onAddProvider && !onAddProviderOrClinic}
            />
          </>
        )}
      </View>
    </CardSurface>
  );
}
